import { useMemo } from "react";
import { motion } from "framer-motion";
import SceneShell from "../components/SceneShell";
import ProductLogo, { GOLD_GRADIENT } from "../components/ProductLogo";

/**
 * Scene 68 — THE FINAL CURTAIN.
 *
 * Stripped, glossy, cinematic. Three product logos centred across the
 * screen — Social Go · Social Pro · Social Week (BETA) — set against a
 * slow-drifting bokeh field. No headlines, no eyebrow, no money particles.
 * Pure brand reveal.
 */

const BOKEH_TONES = [
  "rgba(150,193,31,0.45)", // green
  "rgba(0,222,182,0.45)", // aqua
  "rgba(233,168,32,0.55)", // gold
  "rgba(255,232,168,0.35)", // pale gold
  "rgba(255,255,255,0.25)", // white
];

function Bokeh({ count = 26 }) {
  const dots = useMemo(
    () =>
      Array.from({ length: count }).map((_, i) => {
        const seed = i * 0.617;
        const top = (seed * 73) % 100;
        const left = (seed * 47 + 11) % 100;
        const size = 80 + ((seed * 213) % 240);
        const tone = BOKEH_TONES[i % BOKEH_TONES.length];
        const duration = 14 + ((seed * 31) % 22);
        const delay = (seed * 11) % 8;
        const driftX = -40 + ((seed * 137) % 80);
        const driftY = -40 + ((seed * 89) % 80);
        return { top, left, size, tone, duration, delay, driftX, driftY };
      }),
    [count]
  );

  return (
    <>
      {dots.map((d, i) => (
        <motion.span
          key={i}
          aria-hidden
          className="absolute rounded-full pointer-events-none"
          style={{
            top: `${d.top}%`,
            left: `${d.left}%`,
            width: d.size,
            height: d.size,
            background: `radial-gradient(circle, ${d.tone} 0%, transparent 65%)`,
            filter: "blur(34px)",
            mixBlendMode: "screen",
          }}
          animate={{
            x: [0, d.driftX, 0],
            y: [0, d.driftY, 0],
            opacity: [0.35, 0.85, 0.35],
            scale: [1, 1.18, 1],
          }}
          transition={{
            duration: d.duration,
            delay: d.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}
    </>
  );
}

export default function Scene68_DeployAndScale() {
  return (
    <SceneShell variant="ink" align="center" data-testid="scene-deploy-and-scale">
      {/* Glossy ambient gradient */}
      <motion.div
        className="absolute inset-0 pointer-events-none"
        animate={{
          background: [
            "radial-gradient(ellipse at 30% 60%, rgba(150,193,31,0.18) 0%, transparent 55%), radial-gradient(ellipse at 70% 40%, rgba(0,222,182,0.18) 0%, transparent 55%), radial-gradient(ellipse at 50% 50%, rgba(233,168,32,0.22) 0%, transparent 60%)",
            "radial-gradient(ellipse at 70% 50%, rgba(150,193,31,0.22) 0%, transparent 55%), radial-gradient(ellipse at 30% 50%, rgba(0,222,182,0.22) 0%, transparent 55%), radial-gradient(ellipse at 50% 50%, rgba(233,168,32,0.30) 0%, transparent 60%)",
            "radial-gradient(ellipse at 30% 60%, rgba(150,193,31,0.18) 0%, transparent 55%), radial-gradient(ellipse at 70% 40%, rgba(0,222,182,0.18) 0%, transparent 55%), radial-gradient(ellipse at 50% 50%, rgba(233,168,32,0.22) 0%, transparent 60%)",
          ],
        }}
        transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
      />

      {/* Slow bokeh field */}
      <Bokeh count={26} />

      {/* Subtle film grain */}
      <div className="absolute inset-0 grain pointer-events-none opacity-25" />

      {/* Three product logos centred — Go · Pro · Week (BETA) */}
      <div className="relative w-full">
        <div
          className="flex items-end justify-center gap-12 sm:gap-20 lg:gap-28"
          data-testid="final-product-lockup"
        >
          {/* SOCIAL GO */}
          <motion.div
            initial={{ opacity: 0, x: -40, filter: "blur(12px)" }}
            animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.4, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-end"
          >
            <ProductLogo
              name="GO"
              color="#96C11F"
              fontSize="clamp(28px, 3vw, 56px)"
              iconSize={36}
              testId="final-go"
            />
          </motion.div>

          {/* SOCIAL PRO */}
          <motion.div
            initial={{ opacity: 0, x: -40, filter: "blur(12px)" }}
            animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.4, delay: 1.0, ease: [0.16, 1, 0.3, 1] }}
            className="flex items-end"
          >
            <ProductLogo
              name="PRO"
              color="#e2e8f0"
              fontSize="clamp(28px, 3vw, 56px)"
              iconSize={36}
              testId="final-pro"
            />
          </motion.div>

          {/* SOCIAL WEEK — gold, with a small BETA badge floated to the
              right of the WEEK wordmark so it doesn't push the lockup
              vertically out of line with GO + PRO */}
          <motion.div
            initial={{ opacity: 0, x: -40, filter: "blur(12px)" }}
            animate={{ opacity: 1, x: 0, filter: "blur(0px)" }}
            transition={{ duration: 1.6, delay: 1.7, ease: [0.16, 1, 0.3, 1] }}
            className="relative flex items-end"
            style={{
              filter: "drop-shadow(0 0 60px rgba(233,168,32,0.55))",
            }}
          >
            <ProductLogo
              name="WEEK"
              color="#e9a820"
              gradient={GOLD_GRADIENT}
              fontSize="clamp(28px, 3vw, 56px)"
              iconSize={36}
              iconAccent="#3a2a05"
              testId="final-week"
            />
            <motion.span
              initial={{ opacity: 0, scale: 0.7, x: -8 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.9, delay: 2.8, ease: [0.16, 1, 0.3, 1] }}
              className="absolute rounded-full px-2 py-0.5 text-[8px] sm:text-[9px] tracking-[0.32em] uppercase whitespace-nowrap"
              style={{
                top: "10%",
                right: "-2.6em",
                background:
                  "linear-gradient(135deg, rgba(233,168,32,0.40) 0%, rgba(212,148,10,0.30) 100%)",
                border: "1px solid rgba(233,168,32,0.55)",
                color: "#ffe8a8",
                fontWeight: 700,
                boxShadow: "0 0 14px rgba(233,168,32,0.30)",
              }}
              data-testid="final-week-beta"
            >
              Beta
            </motion.span>
          </motion.div>
        </div>
      </div>
    </SceneShell>
  );
}
